import { useState, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Clock, MoreHorizontal, Pencil, Trash2, CheckCircle2 } from 'lucide-react'
import { WorkDayEntryScheduleDialog } from '@/components/workdays/work-day-entry-schedule-dialog'
import { toast } from 'sonner'

import { assignmentsApi } from '@/services/assignments'
import jobWorkflowApi from '@/services/job-workflow'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { getErrorMessage } from '@/lib/api'
import { useRequiredTenantTimeZone } from '@/lib/tenant-date-time'
import {
  orgWallClockToApiDateTime,
  toEntryDateTimeLocalValue,
  toOrgWallClockDateTimeLocal,
  validateCheckoutAgainstExistingCheckIn,
} from '@/lib/workday-entry-schedule'
import {
  getAssignmentStatusLabel,
} from '@/lib/assignment-status'
import {
  canAdjustWorkDayEntryPay,
  canDeleteWorkDayEntry,
  canEditWorkDayEntrySchedule,
  getWorkDayEntryStatusChangeLabel,
  getWorkDayEntryStatusLabel,
  isFinalWorkDayEntryStatus,
  resolveWorkDayEntryStatusFromEntry,
  type WorkDayEntryStatus,
} from '@/lib/workday-entry-status'
import { withServiceInstanceId } from '@/lib/service-instance'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import { getLocationReturnPath, withReturnTo } from '@/lib/return-navigation'
import type { JobWorkflowTransition } from '@/types'

type WorkDayEntryActionsEntry = {
  id: string | number
  job: string | number
  status?: string | null
  status_label?: string | null
  status_is_final?: boolean
  needs_supervisor_attention?: boolean
  check_in?: string | null
  check_in_local?: string | null
  check_out?: string | null
  check_out_local?: string | null
  org_timezone?: string | null
}

type WorkDayEntryActionsMenuProps = {
  entry: WorkDayEntryActionsEntry
  serviceInstanceId?: string | null
  assignmentId?: string | number | null
  blocked?: boolean
  assignmentBlocked?: boolean
  assignmentStatus?: string | null
  showDelete?: boolean
  alwaysShowTrigger?: boolean
  isDeleting?: boolean
  onDelete?: () => void
  onStatusUpdated?: () => void | Promise<void>
  onStatusChange?: (
    status: WorkDayEntryStatus,
    options?: { checkIn?: string; checkOut?: string; reason?: string },
  ) => void
  // True while the parent's onStatusChange request for this entry is running.
  statusChangePending?: boolean
  allowGroupWorkdayAccess?: boolean
  onEditSchedule?: () => void
  manageScheduleDialog?: boolean
}

const getAttendanceWallClockNow = (timeZone?: string) =>
  toOrgWallClockDateTimeLocal(new Date(), timeZone)

const parseAttendanceWallClockToIso = (value?: string | null, timeZone?: string) => {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) {
    return undefined
  }

  if (timeZone) {
    const iso = orgWallClockToApiDateTime(trimmed, timeZone)
    return iso ?? null
  }

  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString()
}

type TimeCaptureDialogState = {
  transition: JobWorkflowTransition
  field: 'checkIn' | 'checkOut'
  value: string
} | null

type StatusNoteDialogState = {
  transition: JobWorkflowTransition
  checkIn?: string
  checkOut?: string
  note: string
  required: boolean
  isReason: boolean
} | null

const patchWorkDayEntryStatusInCache = (
  current: unknown,
  entryId: string | number,
  nextStatus: string,
) => {
  const normalizedEntryId = String(entryId)
  const nextStatusLabel = getWorkDayEntryStatusLabel(nextStatus)

  const patchItem = (item: Record<string, unknown>) => {
    if (String(item.id ?? '') !== normalizedEntryId) {
      return item
    }
    return {
      ...item,
      status: nextStatus,
      status_label: nextStatusLabel,
    }
  }

  if (Array.isArray(current)) {
    return current.map((item) => {
      if (!item || typeof item !== 'object') {
        return item
      }
      return patchItem(item as Record<string, unknown>)
    })
  }

  if (current && typeof current === 'object') {
    const record = current as Record<string, unknown>

    if (Array.isArray(record.results)) {
      return {
        ...record,
        results: record.results.map((item) => {
          if (!item || typeof item !== 'object') {
            return item
          }
          return patchItem(item as Record<string, unknown>)
        }),
      }
    }

    if ('id' in record) {
      return patchItem(record)
    }
  }

  return current
}

export function WorkDayEntryActionsMenu({
  entry,
  serviceInstanceId,
  assignmentId,
  blocked = false,
  assignmentBlocked = false,
  assignmentStatus,
  showDelete = false,
  alwaysShowTrigger = false,
  isDeleting = false,
  onDelete,
  onStatusChange,
  onStatusUpdated,
  statusChangePending = false,
  allowGroupWorkdayAccess = false,
  onEditSchedule,
  manageScheduleDialog = true,
}: WorkDayEntryActionsMenuProps) {
  const location = useLocation()
  const labels = useWorkspaceLabels()
  const queryClient = useQueryClient()
  // One tenant timezone for default, Now, seed, validation and submit; never the browser zone.
  const { timeZone: tenantTimeZone, isReady: isTenantTimeZoneReady } = useRequiredTenantTimeZone(serviceInstanceId)
  const attendanceTimeZone = tenantTimeZone ?? undefined
  const [timeCaptureDialog, setTimeCaptureDialog] = useState<TimeCaptureDialogState>(null)
  const [statusNoteDialog, setStatusNoteDialog] = useState<StatusNoteDialogState>(null)
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false)
  const entryStatus = resolveWorkDayEntryStatusFromEntry(entry)
  const canEditEntry = canEditWorkDayEntrySchedule(entryStatus) && !blocked
  const canDeleteEntry = showDelete && canDeleteWorkDayEntry(entryStatus) && !blocked && !assignmentBlocked
  const canAdjustPay = canAdjustWorkDayEntryPay(entryStatus) && !blocked && !assignmentBlocked
  const canReviewEntry = Boolean(entry.id) && !blocked
  const statusChangeAssignmentBlocked = assignmentBlocked && !allowGroupWorkdayAccess
  const workflowTransitionsQuery = useQuery({
    queryKey: ['job-workflow-transitions', 'workday_entry', String(entry.id)],
    queryFn: () => jobWorkflowApi.runtime.getStatusTransitions('workday_entry', entry.id),
    enabled: Boolean(entry.id) && !blocked && !statusChangeAssignmentBlocked,
  })
  const cancellationReasonsQuery = useQuery({
    queryKey: ['job-cancellation-reasons', 'workday_entry', String(entry.id)],
    queryFn: () => jobWorkflowApi.runtime.getCancellationReasons('workday_entry', { entity_id: entry.id }),
    enabled: Boolean(entry.id) && !blocked && !statusChangeAssignmentBlocked,
  })
  const entryDetailQuery = useQuery({
    queryKey: ['workday-entry', String(entry.id)],
    queryFn: () => assignmentsApi.getWorkDayEntry(String(entry.id)),
    enabled: Boolean(entry.id) && Boolean(timeCaptureDialog),
    staleTime: 0,
    refetchOnMount: 'always',
  })
  const attendanceEntry = entryDetailQuery.data ?? entry
  const mergedCheckInUtc = attendanceEntry.check_in ?? entry.check_in ?? null
  const shouldAssumeExistingCheckIn = entryStatus === 'in_progress' || entryStatus === 'logged'
  const statusTargets = workflowTransitionsQuery.data ?? []
  const canChangeStatus = statusTargets.length > 0
  const canClearAttention = Boolean(entry.needs_supervisor_attention)
  const hasActions = canEditEntry || canDeleteEntry || canAdjustPay || canReviewEntry || canChangeStatus || canClearAttention || workflowTransitionsQuery.isLoading
  const lockedReason = blocked
    ? assignmentStatus
      ? `Assignment is ${getAssignmentStatusLabel(assignmentStatus)}`
      : 'Entry actions are locked for this job'
    : null

  const adminStatusMutation = useMutation({
    mutationFn: async ({
      transition,
      note,
      checkIn,
      checkOut,
    }: {
      transition: JobWorkflowTransition
      note?: string
      checkIn?: string
      checkOut?: string
    }) => {
      const statusCode = transition.to_status_code || transition.to_status
      if (!statusCode) {
        throw new Error('Missing target status for work day entry override.')
      }

      await assignmentsApi.setWorkDayEntryStatus(String(entry.id), String(statusCode), {
        assignmentId,
        checkIn,
        checkOut,
        reason: note,
      })
    },
    onMutate: ({ transition }) => {
      const nextStatus = String(transition.to_status_code || transition.to_status || '').trim()
      if (!nextStatus) {
        return
      }

      queryClient.setQueryData(['workday-entry', String(entry.id)], (current: unknown) =>
        patchWorkDayEntryStatusInCache(current, entry.id, nextStatus),
      )
      queryClient.setQueriesData({ queryKey: ['workday-entries'] }, (current: unknown) =>
        patchWorkDayEntryStatusInCache(current, entry.id, nextStatus),
      )
      queryClient.setQueriesData({ queryKey: ['job-work-day-entries'] }, (current: unknown) =>
        patchWorkDayEntryStatusInCache(current, entry.id, nextStatus),
      )
      queryClient.setQueriesData({ queryKey: ['worker-work-day-entries'] }, (current: unknown) =>
        patchWorkDayEntryStatusInCache(current, entry.id, nextStatus),
      )
      queryClient.setQueriesData({ queryKey: ['assignment-workday-entries'] }, (current: unknown) =>
        patchWorkDayEntryStatusInCache(current, entry.id, nextStatus),
      )
    },
    onSuccess: async () => {
      toast.success(`${labels.workDayEntry} status updated`)
      queryClient.invalidateQueries({ queryKey: ['job-workflow-transitions', 'workday_entry', String(entry.id)] })
      queryClient.invalidateQueries({ queryKey: ['workday-entries'] })
      queryClient.invalidateQueries({ queryKey: ['workday-entry', String(entry.id)] })
      queryClient.invalidateQueries({ queryKey: ['workday-entry-audits', String(entry.id)] })
      queryClient.invalidateQueries({ queryKey: ['job-work-day-entries'] })
      queryClient.invalidateQueries({ queryKey: ['worker-work-day-entries'] })
      queryClient.invalidateQueries({ queryKey: ['assignment-workday-entries'] })
      queryClient.invalidateQueries({ queryKey: ['attention-work-day-entries'] })
      await queryClient.refetchQueries({ queryKey: ['job-work-day-entries'], type: 'active' })
      await onStatusUpdated?.()
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const statusMutation = useMutation({
    mutationFn: async ({
      transition,
      note,
      checkIn,
      checkOut,
    }: {
      transition: JobWorkflowTransition
      note?: string
      checkIn?: string
      checkOut?: string
    }) => {
      await jobWorkflowApi.runtime.applyStatus({
        entity_type: 'workday_entry',
        entity_id: entry.id,
        to_status: transition.to_status,
        note,
        metadata: {
          ...(assignmentId ? { assignment_id: String(assignmentId) } : {}),
          ...(checkIn ? { check_in: checkIn } : {}),
          ...(checkOut ? { check_out: checkOut } : {}),
        },
      })
    },
    onMutate: ({ transition }) => {
      const nextStatus = String(transition.to_status_code || transition.to_status || '').trim()
      if (!nextStatus) {
        return
      }

      queryClient.setQueryData(['workday-entry', String(entry.id)], (current: unknown) =>
        patchWorkDayEntryStatusInCache(current, entry.id, nextStatus),
      )
      queryClient.setQueriesData({ queryKey: ['workday-entries'] }, (current: unknown) =>
        patchWorkDayEntryStatusInCache(current, entry.id, nextStatus),
      )
      queryClient.setQueriesData({ queryKey: ['job-work-day-entries'] }, (current: unknown) =>
        patchWorkDayEntryStatusInCache(current, entry.id, nextStatus),
      )
      queryClient.setQueriesData({ queryKey: ['worker-work-day-entries'] }, (current: unknown) =>
        patchWorkDayEntryStatusInCache(current, entry.id, nextStatus),
      )
      queryClient.setQueriesData({ queryKey: ['assignment-workday-entries'] }, (current: unknown) =>
        patchWorkDayEntryStatusInCache(current, entry.id, nextStatus),
      )
    },
    onSuccess: async () => {
      toast.success(`${labels.workDayEntry} status updated`)
      queryClient.invalidateQueries({ queryKey: ['job-workflow-transitions', 'workday_entry', String(entry.id)] })
      queryClient.invalidateQueries({ queryKey: ['workday-entries'] })
      queryClient.invalidateQueries({ queryKey: ['workday-entry', String(entry.id)] })
      queryClient.invalidateQueries({ queryKey: ['workday-entry-audits', String(entry.id)] })
      queryClient.invalidateQueries({ queryKey: ['job-work-day-entries'] })
      queryClient.invalidateQueries({ queryKey: ['worker-work-day-entries'] })
      queryClient.invalidateQueries({ queryKey: ['assignment-workday-entries'] })
      queryClient.invalidateQueries({ queryKey: ['attention-work-day-entries'] })
      await queryClient.refetchQueries({ queryKey: ['job-work-day-entries'], type: 'active' })
      await onStatusUpdated?.()
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const clearAttentionMutation = useMutation({
    mutationFn: () => assignmentsApi.clearWorkDayEntryAttention(String(entry.id)),
    onSuccess: () => {
      toast.success('Supervisor attention cleared')
      queryClient.invalidateQueries({ queryKey: ['workday-entries'] })
      queryClient.invalidateQueries({ queryKey: ['workday-entry', String(entry.id)] })
      queryClient.invalidateQueries({ queryKey: ['workday-entry-audits', String(entry.id)] })
      queryClient.invalidateQueries({ queryKey: ['job-work-day-entries'] })
      queryClient.invalidateQueries({ queryKey: ['worker-work-day-entries'] })
      queryClient.invalidateQueries({ queryKey: ['assignment-workday-entries'] })
      queryClient.invalidateQueries({ queryKey: ['attention-work-day-entries'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  if (!hasActions && !alwaysShowTrigger) {
    return blocked
      ? <span className="text-xs text-muted-foreground" title={lockedReason ?? undefined}>Locked</span>
      : null
  }

  const returnTo = getLocationReturnPath(location)
  const adjustPayPath = withReturnTo(
    withServiceInstanceId(
      `/workdays/${entry.id}/edit?focus=pay&job=${encodeURIComponent(String(entry.job))}`,
      serviceInstanceId,
    ),
    returnTo,
  )
  const reviewPath = withReturnTo(
    withServiceInstanceId(
      `/workdays/${entry.id}/edit?focus=review&job=${encodeURIComponent(String(entry.job))}`,
      serviceInstanceId,
    ),
    returnTo,
  )
  const statusLabel = entry.status_label || getWorkDayEntryStatusLabel(entryStatus)
  const statusIsFinal = entry.status_is_final ?? isFinalWorkDayEntryStatus(entryStatus)

  // Seeded from the stored instant in the tenant timezone; check_in_local uses the worker's zone.
  const savedAttendanceCheckIn = useMemo(
    () => (attendanceTimeZone
      ? toEntryDateTimeLocalValue(mergedCheckInUtc, null, attendanceTimeZone)
      : ''),
    [attendanceTimeZone, mergedCheckInUtc],
  )

  const timeCaptureDialogError = useMemo(() => {
    if (!timeCaptureDialog?.value.trim() || !attendanceTimeZone) {
      return null
    }

    if (
      timeCaptureDialog.field === 'checkOut'
      && entryDetailQuery.isFetching
      && !mergedCheckInUtc
      && !shouldAssumeExistingCheckIn
    ) {
      return null
    }

    if (timeCaptureDialog.field === 'checkOut') {
      return validateCheckoutAgainstExistingCheckIn({
        checkInWallClock: savedAttendanceCheckIn,
        checkInUtc: mergedCheckInUtc,
        checkOutWallClock: timeCaptureDialog.value,
        timeZone: attendanceTimeZone,
        assumeExistingCheckIn: shouldAssumeExistingCheckIn,
      })
    }

    return null
  }, [
    attendanceTimeZone,
    entryDetailQuery.isFetching,
    mergedCheckInUtc,
    savedAttendanceCheckIn,
    shouldAssumeExistingCheckIn,
    timeCaptureDialog,
  ])

  const executeStatusChange = (
    transition: JobWorkflowTransition,
    options: { checkIn?: string; checkOut?: string; note?: string } = {},
  ) => {
    const statusCode = transition.to_status_code as WorkDayEntryStatus | undefined
    if (statusCode && statusCode === entryStatus) {
      return
    }

    const note = options.note?.trim()
    if (allowGroupWorkdayAccess) {
      if (onStatusChange && statusCode) {
        onStatusChange(statusCode, {
          checkIn: options.checkIn,
          checkOut: options.checkOut,
          reason: note,
        })
        return
      }

      adminStatusMutation.mutate({
        transition,
        note,
        checkIn: options.checkIn,
        checkOut: options.checkOut,
      })
      return
    }

    if (onStatusChange && statusCode) {
      onStatusChange(statusCode, {
        checkIn: options.checkIn,
        checkOut: options.checkOut,
        reason: note,
      })
      return
    }

    statusMutation.mutate({ transition, note, checkIn: options.checkIn, checkOut: options.checkOut })
  }

  const runStatusChange = (
    transition: JobWorkflowTransition,
    options: { checkIn?: string; checkOut?: string } = {},
  ) => {
    const statusCode = transition.to_status_code as WorkDayEntryStatus | undefined
    if (statusCode && statusCode === entryStatus) {
      return
    }

    const requiresReason = Boolean(transition.to_status_reason_required)
    const requiresNote = Boolean(transition.requires_note)
    if (requiresReason || requiresNote) {
      setStatusNoteDialog({
        transition,
        ...options,
        note: '',
        required: true,
        isReason: requiresReason,
      })
      return
    }

    executeStatusChange(transition, options)
  }

  const handleStatusNoteSubmit = () => {
    if (!statusNoteDialog) {
      return
    }

    const note = statusNoteDialog.note.trim()
    if (statusNoteDialog.required && !note) {
      toast.error(statusNoteDialog.isReason ? 'Reason is required for this status change' : 'A note is required for this status change')
      return
    }

    const { transition, checkIn, checkOut } = statusNoteDialog
    setStatusNoteDialog(null)
    executeStatusChange(transition, { checkIn, checkOut, note })
  }

  const handleStatusChange = (transition: JobWorkflowTransition) => {
    const statusCode = transition.to_status_code as WorkDayEntryStatus | undefined
    if (statusCode && statusCode === entryStatus) {
      return
    }

    if (statusCode === 'in_progress') {
      setTimeCaptureDialog({
        transition,
        field: 'checkIn',
        value: attendanceTimeZone ? getAttendanceWallClockNow(attendanceTimeZone) : '',
      })
      return
    }

    if (statusCode === 'completed' && entryStatus !== 'accepted') {
      void queryClient.fetchQuery({
        queryKey: ['workday-entry', String(entry.id)],
        queryFn: () => assignmentsApi.getWorkDayEntry(String(entry.id)),
        staleTime: 0,
      })

      setTimeCaptureDialog({
        transition,
        field: 'checkOut',
        value: attendanceTimeZone ? getAttendanceWallClockNow(attendanceTimeZone) : '',
      })
      return
    }

    runStatusChange(transition)
  }

  const handleTimeCaptureSubmit = () => {
    if (!timeCaptureDialog || !attendanceTimeZone) {
      return
    }

    const wallClockValue = timeCaptureDialog.value.trim()
    if (!wallClockValue) {
      toast.error(`Enter a ${timeCaptureDialog.field === 'checkIn' ? 'check-in' : 'check-out'} time.`)
      return
    }

    if (timeCaptureDialogError) {
      toast.error(timeCaptureDialogError)
      return
    }

    const parsed = parseAttendanceWallClockToIso(wallClockValue, attendanceTimeZone)
    if (parsed === null) {
      toast.error(`Invalid ${timeCaptureDialog.field === 'checkIn' ? 'check-in' : 'check-out'} datetime.`)
      return
    }

    const options = timeCaptureDialog.field === 'checkIn'
      ? { checkIn: parsed }
      : { checkOut: parsed }
    const transition = timeCaptureDialog.transition
    setTimeCaptureDialog(null)
    runStatusChange(transition, options)
  }

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            aria-label={`${labels.workDayEntry} actions`}
            title={!hasActions ? (lockedReason ?? 'No actions available') : lockedReason ?? undefined}
          >
            {(statusMutation.isPending || adminStatusMutation.isPending || clearAttentionMutation.isPending || statusChangePending) ? <Spinner className="h-4 w-4" /> : <MoreHorizontal className="h-4 w-4" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-80 w-56 overflow-y-auto rounded-lg">
        {!hasActions ? (
          <div className="px-2 py-2 text-xs text-muted-foreground">
            {lockedReason ?? `No actions available for this ${labels.workDayEntryLower}.`}
          </div>
        ) : null}
        {canClearAttention ? (
          <>
            <DropdownMenuItem
              disabled={clearAttentionMutation.isPending}
              onSelect={(event) => {
                event.preventDefault()
                clearAttentionMutation.mutate()
              }}
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Clear attention
            </DropdownMenuItem>
            {canChangeStatus || canEditEntry || canAdjustPay || canReviewEntry || canDeleteEntry ? <DropdownMenuSeparator /> : null}
          </>
        ) : null}
        {canChangeStatus ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              {allowGroupWorkdayAccess ? 'Change status (admin override)' : 'Change status'}
            </DropdownMenuLabel>
            <DropdownMenuItem disabled className="text-xs opacity-80">
              Current: {statusLabel}
              {statusIsFinal ? ' (final)' : ''}
            </DropdownMenuItem>
            {workflowTransitionsQuery.isLoading ? (
              <DropdownMenuItem disabled>Loading workflow actions...</DropdownMenuItem>
            ) : null}
            {statusTargets.map((transition) => {
              const statusCode = transition.to_status_code as WorkDayEntryStatus | undefined
              const label = statusCode
                ? getWorkDayEntryStatusChangeLabel(statusCode)
                : transition.to_status_label || String(transition.to_status)
              return (
              <DropdownMenuItem
                key={transition.id}
                disabled={statusMutation.isPending || adminStatusMutation.isPending || statusChangePending}
                onSelect={(event) => {
                  event.preventDefault()
                  handleStatusChange(transition)
                }}
              >
                {statusChangePending ? <Spinner className="mr-2 h-4 w-4" /> : null}
                {label}
                {transition.to_status_reason_required ? ' (reason)' : ''}
              </DropdownMenuItem>
              )
            })}
            {canEditEntry || canAdjustPay || canReviewEntry || canDeleteEntry ? <DropdownMenuSeparator /> : null}
          </>
        ) : null}
        {canEditEntry ? (
          <DropdownMenuItem
            onSelect={() => {
              if (onEditSchedule) {
                onEditSchedule()
              } else {
                setIsScheduleDialogOpen(true)
              }
            }}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Edit {labels.workDayEntry}
          </DropdownMenuItem>
        ) : null}
        {canAdjustPay ? (
          <DropdownMenuItem asChild>
            <Link to={adjustPayPath}>
              <Clock className="mr-2 h-4 w-4" />
              Adjust pay
            </Link>
          </DropdownMenuItem>
        ) : null}
        {canReviewEntry ? (
          <DropdownMenuItem asChild>
            <Link to={reviewPath}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Review approvals
            </Link>
          </DropdownMenuItem>
        ) : null}
        {canDeleteEntry ? (
          <DropdownMenuItem
            className="text-destructive"
            disabled={isDeleting}
            onClick={onDelete}
          >
            {isDeleting ? <Spinner className="mr-2 h-4 w-4" /> : <Trash2 className="mr-2 h-4 w-4" />}
            Delete
          </DropdownMenuItem>
        ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      {manageScheduleDialog ? (
      <WorkDayEntryScheduleDialog
        entryId={String(entry.id)}
        jobId={String(entry.job)}
        serviceInstanceId={serviceInstanceId}
        open={isScheduleDialogOpen}
        onOpenChange={setIsScheduleDialogOpen}
        onSaved={onStatusUpdated}
      />
      ) : null}

      <Dialog open={Boolean(timeCaptureDialog)} onOpenChange={(open) => {
        if (!open) {
          setTimeCaptureDialog(null)
        }
      }}>
        <DialogContent className="z-[120] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {timeCaptureDialog?.field === 'checkOut' ? 'Set Check-Out Time' : 'Set Check-In Time'}
            </DialogTitle>
            <DialogDescription>
              Confirm the actual time for this {labels.workDayEntryLower} using the organization timezone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor={`workday-${timeCaptureDialog?.field ?? 'time'}`}>
              {timeCaptureDialog?.field === 'checkOut' ? 'Check-out time' : 'Check-in time'}
            </Label>
            <div className="flex gap-2">
              <Input
                id={`workday-${timeCaptureDialog?.field ?? 'time'}`}
                type="datetime-local"
                className="flex-1"
                value={timeCaptureDialog?.value ?? ''}
                onChange={(event) => {
                  setTimeCaptureDialog((current) => current
                    ? { ...current, value: event.target.value }
                    : current)
                }}
                autoFocus
              />
              <Button
                type="button"
                variant="outline"
                disabled={!isTenantTimeZoneReady}
                onClick={() => {
                  setTimeCaptureDialog((current) => current && attendanceTimeZone
                    ? { ...current, value: getAttendanceWallClockNow(attendanceTimeZone) }
                    : current)
                }}
              >
                Now
              </Button>
            </div>
            {savedAttendanceCheckIn && timeCaptureDialog?.field === 'checkOut' ? (
              <p className="text-xs text-muted-foreground">
                Existing check-in: {savedAttendanceCheckIn.replace('T', ' ')}
              </p>
            ) : mergedCheckInUtc && timeCaptureDialog?.field === 'checkOut' ? (
              <p className="text-xs text-muted-foreground">
                Existing check-in is already recorded for this entry.
              </p>
            ) : shouldAssumeExistingCheckIn && timeCaptureDialog?.field === 'checkOut' ? (
              <p className="text-xs text-muted-foreground">
                Check-in was recorded when this entry moved to in progress.
              </p>
            ) : null}
            {entryDetailQuery.isFetching && timeCaptureDialog?.field === 'checkOut' && !mergedCheckInUtc ? (
              <p className="text-xs text-muted-foreground">Loading check-in details...</p>
            ) : null}
            {timeCaptureDialogError ? (
              <p className="text-sm text-destructive">{timeCaptureDialogError}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Times use the organization timezone, not your browser clock.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setTimeCaptureDialog(null)}
              disabled={statusMutation.isPending || adminStatusMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleTimeCaptureSubmit}
              disabled={
                !isTenantTimeZoneReady
                || statusMutation.isPending
                || adminStatusMutation.isPending
                || Boolean(timeCaptureDialogError)
                || (timeCaptureDialog?.field === 'checkOut'
                  && entryDetailQuery.isFetching
                  && !mergedCheckInUtc
                  && !shouldAssumeExistingCheckIn)
              }
            >
              {(statusMutation.isPending || adminStatusMutation.isPending) ? <Spinner className="mr-2 h-4 w-4" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(statusNoteDialog)} onOpenChange={(open) => {
        if (!open) {
          setStatusNoteDialog(null)
        }
      }}>
        <DialogContent className="z-[120] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {statusNoteDialog?.isReason ? 'Add Reason' : 'Add Note'}
            </DialogTitle>
            <DialogDescription>
              {statusNoteDialog?.isReason
                ? 'Enter the reason for this status change before continuing.'
                : 'Add a note that explains this status change.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm">
              <p className="text-xs text-muted-foreground">Changing to</p>
              <p className="font-medium">
                {statusNoteDialog?.transition.to_status_code
                  ? getWorkDayEntryStatusChangeLabel(statusNoteDialog.transition.to_status_code as WorkDayEntryStatus)
                  : statusNoteDialog?.transition.to_status_label || statusNoteDialog?.transition.to_status}
              </p>
            </div>

            {statusNoteDialog?.isReason && (cancellationReasonsQuery.data?.length ?? 0) > 0 ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <p className="mb-1 font-medium">Available reasons</p>
                <div className="flex flex-wrap gap-1.5">
                  {cancellationReasonsQuery.data?.map((reason) => (
                    <button
                      key={String(reason.id ?? reason.code ?? reason.label)}
                      type="button"
                      className="rounded-full border border-amber-200 bg-white px-2 py-1 text-amber-900 hover:bg-amber-100"
                      onClick={() => setStatusNoteDialog((current) => current ? { ...current, note: String(reason.label ?? '') } : current)}
                    >
                      {reason.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="workday-status-note">
                {statusNoteDialog?.isReason ? 'Reason' : 'Note'}
              </Label>
              <Textarea
                id="workday-status-note"
                value={statusNoteDialog?.note ?? ''}
                onChange={(event) => setStatusNoteDialog((current) => current ? { ...current, note: event.target.value } : current)}
                placeholder={statusNoteDialog?.isReason ? 'Why is this status changing?' : 'Add details for this status change'}
                rows={4}
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setStatusNoteDialog(null)}
              disabled={statusMutation.isPending || adminStatusMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleStatusNoteSubmit}
              disabled={statusMutation.isPending || adminStatusMutation.isPending}
            >
              {(statusMutation.isPending || adminStatusMutation.isPending) ? <Spinner className="mr-2 h-4 w-4" /> : null}
              Confirm status change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export function getWorkDayEntryActionsBlocked(options: {
  assignmentStatus?: string | null
  jobBlocked?: boolean
}) {
  return Boolean(options.jobBlocked)
}
